import { useOrders, useSettings } from '@restaurant/api-client/react';
import { formatMoney, ORDER_STATUS_LABEL, type OrderDto, type OrderStatus } from '@restaurant/shared';
import { Bike, ClipboardList, ShoppingBag, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { EmptyState, PageHeader, Skeleton } from '@/components/ui/misc';
import { cn, todayYmd } from '@/lib/utils';
import { OrderActions } from './OrderActions';
import { OrderDrawer } from './OrderDrawer';
import { NewOrderDialog } from './NewOrderDialog';
import { ageMinutes, STATUS_STYLE } from './format';

const COLUMNS: { key: string; title: string; statuses: OrderStatus[] }[] = [
  { key: 'new', title: 'New drafts', statuses: ['draft'] },
  { key: 'confirmed', title: 'Confirmed', statuses: ['confirmed'] },
  { key: 'kitchen', title: 'Preparing', statuses: ['preparing'] },
  { key: 'ready', title: 'Ready / on the way', statuses: ['ready', 'out_for_delivery'] },
  { key: 'done', title: 'Done', statuses: ['completed', 'cancelled'] },
];

export function OrdersPage() {
  const { data: settings } = useSettings();
  const [date, setDate] = useState(todayYmd(settings?.timezone));
  const [params, setParams] = useSearchParams();
  const openId = params.get('order');
  const orders = useOrders({ date, limit: 200 });
  const drafts = useOrders({ status: 'draft', limit: 50 }); // drafts from earlier days still need attention
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000); // refresh elapsed-time colouring
    return () => clearInterval(t);
  }, []);

  const all = useMemo(() => {
    const map = new Map<string, OrderDto>();
    for (const o of [...(drafts.data ?? []), ...(orders.data ?? [])]) map.set(o.id, o);
    return [...map.values()];
  }, [orders.data, drafts.data]);
  const currency = settings?.currency ?? 'LKR';
  const open = (id: string | null) => setParams(id ? { order: id } : {}, { replace: true });

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Orders"
        description={`${plural(all.filter((o) => o.status !== 'cancelled' && o.status !== 'draft').length, 'order')} · ${formatMoney(all.filter((o) => o.status === 'completed').reduce((s, o) => s + o.totalCents, 0), currency)} completed`}
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" aria-label="Business date" />
            <NewOrderDialog onCreated={(o) => void orders.refetch().then(() => open(o.id))} />
          </div>
        }
      />
      {orders.isLoading ? (
        <div className="grid flex-1 gap-3 p-4 md:grid-cols-5">{COLUMNS.map((c) => <Skeleton key={c.key} className="h-64" />)}</div>
      ) : all.length === 0 ? (
        <EmptyState icon={<ClipboardList />} title="No orders for this day" description="Orders from chats (manual or AI) and from the New order button show up here." />
      ) : (
        <div className="grid min-h-0 flex-1 auto-cols-[minmax(210px,1fr)] grid-flow-col gap-3 overflow-x-auto p-4">
          {COLUMNS.map((col) => {
            const list = all.filter((o) => col.statuses.includes(o.status)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
            return (
              <section key={col.key} className="flex min-h-0 flex-col rounded-xl bg-muted/60">
                <h2 className="flex items-center justify-between px-3 py-2 text-sm font-semibold">
                  {col.title} <span className="rounded-full bg-card px-2 text-xs text-muted-foreground">{list.length}</span>
                </h2>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                  {list.map((o) => <OrderCard key={o.id} o={o} currency={currency} onOpen={() => open(o.id)} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}
      <OrderDrawer orderId={openId} onClose={() => open(null)} />
    </div>
  );
}

function OrderCard({ o, currency, onOpen }: { o: OrderDto; currency: string; onOpen: () => void }) {
  const active = !['completed', 'cancelled'].includes(o.status);
  const age = ageMinutes(o.confirmedAt ?? o.createdAt);
  const late = active && o.status !== 'draft' && age > 30;
  return (
    <div className={cn('rounded-lg border bg-card p-3 shadow-xs', late && 'border-destructive/60', o.status === 'draft' && 'border-warning/60')}>
      <button onClick={onOpen} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">#{o.number}</span>
          <span className="flex items-center gap-1.5">
            {o.source === 'ai' ? <Sparkles className="size-3.5 text-primary" aria-label="AI" /> : null}
            {o.type === 'delivery' ? <Bike className="size-4 text-muted-foreground" aria-label="Delivery" /> : <ShoppingBag className="size-4 text-muted-foreground" aria-label="Pickup" />}
            <Badge variant={STATUS_STYLE[o.status].variant}>{ORDER_STATUS_LABEL[o.status]}</Badge>
          </span>
        </div>
        <p className="truncate text-sm text-muted-foreground">{o.customer.name ?? `+${o.customer.waId}`}</p>
        <ul className="mt-1.5 space-y-0.5 text-sm">
          {o.items.slice(0, 4).map((i) => <li key={i.id} className="truncate"><span className="tabular-nums">{i.quantity}×</span> {i.name}</li>)}
          {o.items.length > 4 ? <li className="text-xs text-muted-foreground">+{o.items.length - 4} more</li> : null}
        </ul>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className={cn(late && 'font-medium text-destructive')}>{active ? `${age} min` : ''}</span>
          <span className="font-medium text-foreground tabular-nums">{formatMoney(o.totalCents, currency)}</span>
        </div>
        {o.aiUnclear.length ? <p className="mt-1 text-xs text-warning">⚠ {o.aiUnclear.length} thing(s) to check</p> : null}
      </button>
      {active ? <div className="mt-2 border-t pt-2"><OrderActions order={o} /></div> : null}
    </div>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
